import {
	Alert,
	AlertIcon,
	Box,
	Button,
	Flex,
	FormControl,
	FormErrorMessage,
	FormLabel,
	Heading,
	Icon,
	IconButton,
	Input,
	InputGroup,
	InputRightElement,
	Stat,
	StatLabel,
	StatNumber,
	Text
} from '@chakra-ui/react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdVisibility, MdVisibilityOff } from 'react-icons/md';
import { TbDatabaseSearch } from 'react-icons/tb';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { RootState } from '../../../store/store';
import { login } from '../../../store/features/auth/auth.slice';

const validationSchema = Yup.object({
	email: Yup.string().email('E-mail inválido').required('Informe o e-mail'),
	password: Yup.string().required('Informe a senha')
});

export default function SignIn() {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const [showPassword, setShowPassword] = useState(false);
	const { error, loading, authenticated } = useAppSelector((state: RootState) => state.auth);

	useEffect(() => {
		if (authenticated) {
			navigate('/admin/consulta');
		}
	}, [authenticated, navigate]);

	const formik = useFormik({
		initialValues: { email: '', password: '' },
		validationSchema,
		onSubmit: async values => {
			const result = await dispatch(login(values));
			if ((result as any).meta?.requestStatus === 'fulfilled') {
				navigate('/admin/consulta');
			}
		}
	});

	return (
		<Flex w='100%' minH='100vh'>
			{/* Left navy panel */}
			<Flex
				display={{ base: 'none', md: 'flex' }}
				direction='column'
				justify='space-between'
				w={{ md: '45%', lg: '42%' }}
				bg='navy.900'
				color='white'
				p='60px'>
				<Flex align='center' gap='12px'>
					<Flex align='center' justify='center' w='44px' h='44px' borderRadius='12px' bg='brand.500'>
						<Icon as={TbDatabaseSearch} color='white' w='24px' h='24px' />
					</Flex>
					<Text fontSize='28px' fontWeight='800' letterSpacing='-1px'>
						Datric
					</Text>
				</Flex>

				<Box>
					<Heading fontSize={{ md: '40px', lg: '48px' }} lineHeight='1.1' fontWeight='800'>
						Dados higienizados, prontos para vender.
					</Heading>
					<Text mt='20px' color='#9FC0F0' fontSize='lg' maxW='420px'>
						Enriqueça e valide sua base de CPFs em segundos: renda, score, telefones com WhatsApp,
						endereços e muito mais.
					</Text>

					<Flex mt='40px' gap='40px'>
						<Stat>
							<StatNumber fontSize='32px' color='cyan.300'>
								+50M
							</StatNumber>
							<StatLabel color='#9FC0F0'>CPFs na base</StatLabel>
						</Stat>
						<Stat>
							<StatNumber fontSize='32px' color='cyan.300'>
								98%
							</StatNumber>
							<StatLabel color='#9FC0F0'>Taxa de acerto</StatLabel>
						</Stat>
					</Flex>
				</Box>

				<Text color='#4E6A9C' fontSize='sm'>
					&copy; {new Date().getFullYear()} Datric
				</Text>
			</Flex>

			{/* Right form */}
			<Flex flex='1' align='center' justify='center' bg='white' p={{ base: '24px', md: '40px' }}>
				<Box w='100%' maxW='420px'>
					<Heading color='navy.700' fontSize='32px' mb='6px'>
						Entrar
					</Heading>
					<Text color='secondaryGray.700' mb='30px'>
						Acesse sua conta para consultar e higienizar leads.
					</Text>

					{error && (
						<Alert status='error' borderRadius='12px' mb='20px'>
							<AlertIcon />
							E-mail ou senha inválidos.
						</Alert>
					)}

					<form onSubmit={formik.handleSubmit}>
						<FormControl isInvalid={!!(formik.touched.email && formik.errors.email)} mb='20px'>
							<FormLabel color='navy.700' fontWeight='600' fontSize='sm'>
								E-mail
							</FormLabel>
							<Input
								name='email'
								type='email'
								variant='auth'
								size='lg'
								placeholder='voce@empresa.com'
								value={formik.values.email}
								onChange={formik.handleChange}
								onBlur={formik.handleBlur}
							/>
							<FormErrorMessage>{formik.errors.email}</FormErrorMessage>
						</FormControl>

						<FormControl isInvalid={!!(formik.touched.password && formik.errors.password)} mb='28px'>
							<FormLabel color='navy.700' fontWeight='600' fontSize='sm'>
								Senha
							</FormLabel>
							<InputGroup size='lg'>
								<Input
									name='password'
									type={showPassword ? 'text' : 'password'}
									variant='auth'
									placeholder='********'
									value={formik.values.password}
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
								/>
								<InputRightElement>
									<IconButton
										aria-label='mostrar senha'
										variant='ghost'
										icon={<Icon as={showPassword ? MdVisibilityOff : MdVisibility} />}
										onClick={() => setShowPassword(!showPassword)}
									/>
								</InputRightElement>
							</InputGroup>
							<FormErrorMessage>{formik.errors.password}</FormErrorMessage>
						</FormControl>

						<Button
							type='submit'
							variant='brand'
							w='100%'
							size='lg'
							isLoading={loading}
							loadingText='Entrando...'>
							Entrar
						</Button>
					</form>
				</Box>
			</Flex>
		</Flex>
	);
}
